import React, { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react';
import type { User } from '../types';
import { authAPI, adminAPI, staffAPI } from '../services/api';
import { toast } from 'react-toastify';
import { updateFavicon } from '../utils/faviconUtils';

interface ClinicSettings {
  CLINIC_NAME?: string;
  CLINIC_LOGO?: string;
  CLINIC_FAVICON?: string;
  CLINIC_TAGLINE?: string;
  CLINIC_COLOR_PRIMARY?: string;
  CLINIC_COLOR_SECONDARY?: string;
  CLINIC_COLOR_ACCENT?: string;
  general_timezone?: string;
  general_currency?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  clinic: ClinicSettings | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isRefreshingClinic: boolean;
  isRefreshingPermissions: boolean; // Add permission loading state
  userPermissions: string[]; // Add user permissions
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_REFRESHING_CLINIC'; payload: boolean }
  | { type: 'SET_REFRESHING_PERMISSIONS'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string; clinic?: ClinicSettings } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'UPDATE_CLINIC'; payload: ClinicSettings }
  | { type: 'SET_PERMISSIONS'; payload: string[] };

const initialState: AuthState = {
  user: null,
  token: null,
  clinic: null,
  isLoading: true,
  isAuthenticated: false,
  isRefreshingClinic: false,
  isRefreshingPermissions: false, // Add permission loading state
  userPermissions: [], // Add user permissions to state
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_REFRESHING_CLINIC':
      return { ...state, isRefreshingClinic: action.payload };
    
    case 'SET_REFRESHING_PERMISSIONS':
      return { ...state, isRefreshingPermissions: action.payload };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        clinic: action.payload.clinic || null,
        isLoading: false,
        isAuthenticated: true,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        clinic: null,
        isLoading: false,
        isAuthenticated: false,
        isRefreshingClinic: false,
        isRefreshingPermissions: false, // Clear permission loading state
        userPermissions: [], // Clear permissions on logout
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    
    case 'UPDATE_CLINIC':
      // Update favicon when clinic settings are updated
      const faviconUrl = action.payload.CLINIC_FAVICON || action.payload.CLINIC_LOGO;
      if (faviconUrl) {
        updateFavicon(faviconUrl);
      }
      return {
        ...state,
        clinic: action.payload,
        isRefreshingClinic: false,
      };
    
    case 'SET_PERMISSIONS':
      return {
        ...state,
        userPermissions: action.payload,
        isRefreshingPermissions: false, // Clear permission loading state
      };
    
    default:
      return state;
  }
};

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  updateClinic: (clinic: ClinicSettings) => void;
  refreshProfile: () => Promise<void>;
  refreshClinicSettings: () => Promise<void>;
  fetchUserPermissions: (userId: string) => Promise<string[]>;
  hasRole: (roles: string | string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  userPermissions: string[]; // Add userPermissions to interface
  isRefreshingPermissions: boolean; // Add permission loading state to interface
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return a fallback context instead of throwing an error
    return {
      user: null,
      token: null,
      clinic: null,
      isLoading: false,
      isAuthenticated: false,
      isRefreshingClinic: false,
      isRefreshingPermissions: false,
      userPermissions: [],
      login: async () => { throw new Error('AuthProvider not available'); },
      logout: () => {},
      updateUser: () => {},
      updateClinic: () => {},
      refreshProfile: async () => { throw new Error('AuthProvider not available'); },
      refreshClinicSettings: async () => { throw new Error('AuthProvider not available'); },
      fetchUserPermissions: async () => { throw new Error('AuthProvider not available'); },
      hasRole: () => false,
      hasPermission: () => false,
    };
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userStr = localStorage.getItem('user');
        const clinicStr = localStorage.getItem('clinic');
        
        if (token && userStr) {
          const user = JSON.parse(userStr);
          const clinic = clinicStr ? JSON.parse(clinicStr) : null;
          
          // Update favicon if logo/favicon is available from localStorage
          if (clinic) {
            const faviconUrl = clinic.CLINIC_FAVICON || clinic.CLINIC_LOGO;
            if (faviconUrl) {
              updateFavicon(faviconUrl);
            }
          }
          
          // First, set the user state from localStorage to avoid logout on refresh
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user, token, clinic }
          });

          // Immediately fetch user permissions to avoid menu loading issues
          try {
            const permissions = await fetchUserPermissions(user.id);
            // Permissions are already set in fetchUserPermissions function
          } catch (error) {
            // Continue without permissions - will use fallback
          }

          // Then verify the token with the backend
          try {
            await authAPI.getProfile();
          } catch (error) {
            // If token validation fails, clear the auth state
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('clinic');
            dispatch({ type: 'LOGOUT' });
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        // Silent failover
        // Clear any corrupted auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('clinic');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await authAPI.login({ email, password });
      const { user, token, clinic } = response.data.data;
      
      
      // Store in localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      if (clinic) {
        localStorage.setItem('clinic', JSON.stringify(clinic));
        // Update favicon if logo/favicon is available
        const faviconUrl = clinic.CLINIC_FAVICON || clinic.CLINIC_LOGO;
        if (faviconUrl) {
          updateFavicon(faviconUrl);
        }
      }
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token, clinic }
      });
      
      // Fetch user permissions after successful login
      try {
        const permissions = await fetchUserPermissions(user.id);
        // Permissions are already set in fetchUserPermissions function
      } catch (error) {
        // Continue without permissions - will use fallback
      }
      
      // Only refresh clinic settings if we don't have clinic data
      if (!clinic) {
        try {
          await refreshClinicSettings();
        } catch (error) {
          console.warn('Failed to refresh clinic settings on login:', error);
        }
      }
      
      toast.success(`Welcome back, ${user.profile?.firstName || user.email}!`);
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('clinic');
    dispatch({ type: 'LOGOUT' });
    toast.info('You have been logged out');
  };

  const updateUser = (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const updateClinic = (clinic: ClinicSettings) => {
    localStorage.setItem('clinic', JSON.stringify(clinic));
    dispatch({ type: 'UPDATE_CLINIC', payload: clinic });
  };

  const refreshProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      const { user } = response.data.data;
      
      // Update localStorage and state
      localStorage.setItem('user', JSON.stringify(user));
      dispatch({ type: 'UPDATE_USER', payload: user });
      
      // silent
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      throw error;
    }
  };

  const refreshClinicSettings = async () => {
    // Prevent multiple simultaneous calls
    if (state.isRefreshingClinic) {
      return;
    }

    try {
      dispatch({ type: 'SET_REFRESHING_CLINIC', payload: true });
      
      const response = await adminAPI.getClinicBranding();
      const clinicData = response.data.data || {};
      
      // Update localStorage and state
      localStorage.setItem('clinic', JSON.stringify(clinicData));
      dispatch({ type: 'UPDATE_CLINIC', payload: clinicData });
      
      // Update favicon if logo/favicon is available
      const faviconUrl = clinicData.CLINIC_FAVICON || clinicData.CLINIC_LOGO;
      if (faviconUrl) {
        updateFavicon(faviconUrl);
      }
      
      // silent
    } catch (error) {
      
      // Load default clinic data when backend fails
      const defaultClinicData = {
        CLINIC_NAME: 'Modern Clinic Management System',
        CLINIC_TAGLINE: 'Healthcare Management System',
        CLINIC_LOGO: null,
        CLINIC_ADDRESS: '123 Healthcare Street, Medical City',
        CLINIC_PHONE: '+1 (555) 123-4567',
        CLINIC_EMAIL: 'info@modernclinic.com',
        CLINIC_WEBSITE: 'https://modernclinic.com'
      };
      
      localStorage.setItem('clinic', JSON.stringify(defaultClinicData));
      dispatch({ type: 'UPDATE_CLINIC', payload: defaultClinicData });
    }
  };

  const fetchUserPermissions = async (userId: string) => {
    try {
      dispatch({ type: 'SET_REFRESHING_PERMISSIONS', payload: true });
      const response = await staffAPI.getUserPermissions();
      
      // Extract permission names from the response
      // Backend returns: { success: true, data: [permissions] }
      // Permissions have format: { resource: 'admissions', action: 'create', name: 'Create Admissions', ... }
      // Map to 'resource:action' format for hasPermission checks
      const permissions = response.data.data?.map((p: any) => 
        p.resource && p.action ? `${p.resource}:${p.action}` : p.name
      ) || [];
      
      // If we got permissions, use them; otherwise clear to use fallback
      if (permissions.length > 0) {
        dispatch({ type: 'SET_PERMISSIONS', payload: permissions });
      } else {
        // Clear permissions to trigger fallback logic
        dispatch({ type: 'SET_PERMISSIONS', payload: [] });
      }
      return permissions;
    } catch (error) {
      // On error, clear permissions to use fallback
      dispatch({ type: 'SET_PERMISSIONS', payload: [] });
      dispatch({ type: 'SET_REFRESHING_PERMISSIONS', payload: false });
      return [];
    }
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!state.user) {
      return false;
    }
    
    const roleArray = Array.isArray(roles) ? roles : [roles];
    const hasRoleResult = roleArray.includes(state.user.role);
    return hasRoleResult;
  };

  const hasPermission = (permission: string): boolean => {
    if (!state.user) return false;
    
    // For pharmacists, ensure pharmacy permissions are always available
    if (state.user.role === 'PHARMACIST' && permission.startsWith('pharmacy:')) {
      return true;
    }
    
    // For doctors and receptionists, always use fallback permissions to ensure access
    // This fixes the issue where backend permissions might be incomplete
    if (state.user.role === 'DOCTOR' || state.user.role === 'RECEPTIONIST' || state.user.role === 'REPORTS') {
      // Fall through to fallback logic
    } else if (state.userPermissions.length > 0) {
      // Use real permissions from backend for other roles
      // Check for admin wildcard permission first
      if (state.userPermissions.includes('*:*')) {
        return true;
      }
      
      const hasPermissionResult = state.userPermissions.includes(permission);
      return hasPermissionResult;
    }
    
    // Fallback to hardcoded permissions for backward compatibility
    // This ensures menu items show immediately even if permissions are still loading
    const userRole = state.user.role;
    
    // Define role-based permissions (matching backend seed-users.js)
    const permissions: Record<string, string[]> = {
      ADMIN: [
        'patients:create', 'patients:read', 'patients:update', 'patients:delete',
        'appointments:create', 'appointments:read', 'appointments:update', 'appointments:cancel',
        'treatments:create', 'treatments:read', 'treatments:update',
        'prescriptions:create', 'prescriptions:read', 'prescriptions:update', 'prescriptions:delete',
        'lab-orders:create', 'lab-orders:read', 'lab-orders:update',
        'billing:create', 'billing:read', 'billing:update',
        'staff:create', 'staff:read', 'staff:update', 'staff:delete',
        'medical-records:create', 'medical-records:read', 'medical-records:update',
        'pharmacy:read', 'pharmacy:manage',
        'inventory:read', 'inventory:update',
        'reports:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'waiting-patients:view', 'waiting-patients:update',
        'doctor-triage:view', 'doctor-triage:assign', 'doctor-triage:update',
        'pharmacy-queue:view', 'pharmacy-queue:process',
        'lab-queue:view', 'lab-queue:process',
        'verification-queue:view', 'verification-queue:verify',
        'treatments:view', 'treatments:create', 'treatments:update',
        'checkout:view', 'checkout:process',
        // Legacy permissions (for backward compatibility)
        'workflow:view', 'workflow:update',
        'patient-queue:view', 'patient-queue:assign', 'patient-queue:update',
        'waiting-room:view',
        'triage:view', 'triage:assign', 'triage:update',
        'checkin:view', 'checkin:update',
        'vitals:record', 'vitals:view', 'vitals:update',
        'patient-call:initiate',
        'waiting-time:view',
        'verification:read',
        '*:*' // Admin has all permissions
      ],
      
      DOCTOR: [
        'patients:read', 'patients:update',
        'appointments:read', 'appointments:update',
        'treatments:create', 'treatments:read', 'treatments:update',
        'prescriptions:create', 'prescriptions:read', 'prescriptions:update',
        'lab-orders:create', 'lab-orders:read', 'lab-orders:update',
        'medical-records:create', 'medical-records:read', 'medical-records:update',
        'pharmacy:read', 'pharmacy:create', 'pharmacy:manage',
        'reports:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'waiting-patients:view',
        'doctor-triage:view', 'doctor-triage:assign', 'doctor-triage:update',
        'pharmacy-queue:view',
        'lab-queue:view',
        'verification-queue:view',
        'treatments:view', 'treatments:create', 'treatments:update',
        'checkout:view',
        // Legacy permissions (for backward compatibility)
        'waiting-room:view',
        'patient-queue:view', 'patient-queue:assign',
        'triage:view', 'triage:assign', 'triage:update',
        'workflow:view', 'workflow:update',
        'patient-call:initiate',
        'vitals:view',
        'checkin:view',
        'waiting-time:view',
        'verification:read'
      ],
      
      NURSE: [
        'patients:read', 'patients:update',
        'appointments:read',
        'treatments:read', 'treatments:update',
        'prescriptions:read',
        'lab-orders:read', 'lab-orders:update',
        'medical-records:read', 'medical-records:update',
        'pharmacy:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'waiting-patients:view', 'waiting-patients:update',
        'doctor-triage:view',
        'pharmacy-queue:view',
        'lab-queue:view',
        'verification-queue:view',
        'treatments:view', 'treatments:update',
        'checkout:view',
        // Legacy permissions (for backward compatibility)
        'waiting-room:view',
        'patient-queue:view', 'patient-queue:update',
        'patient-call:initiate',
        'vitals:record', 'vitals:view', 'vitals:update',
        'checkin:view', 'checkin:update',
        'waiting-time:view'
      ],
      
      PHARMACIST: [
        'patients:read',
        'prescriptions:create', 'prescriptions:read', 'prescriptions:update',
        'pharmacy:read', 'pharmacy:create', 'pharmacy:manage',
        'inventory:read', 'inventory:update',
        'reports:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'pharmacy-queue:view', 'pharmacy-queue:process',
        'verification-queue:view', 'verification-queue:verify',
        'checkout:view',
        // Legacy permissions (for backward compatibility)
        'verification:read'
      ],
      
      LAB_TECHNICIAN: [
        'patients:read', 'patients:update',
        'appointments:read',
        'lab-orders:create', 'lab-orders:read', 'lab-orders:update', 'lab-orders:delete',
        'laboratory:read', 'laboratory:manage',
        'medical-records:read', 'medical-records:update',
        'treatments:read', 'treatments:update',
        'prescriptions:read',
        'admissions:read', 'admissions:view',
        'reports:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'lab-queue:view', 'lab-queue:process',
        'verification-queue:view', 'verification-queue:verify',
        'checkout:view',
        // Legacy permissions (for backward compatibility)
        'verification:read',
        'workflow:view', 'workflow:update',
        'vitals:record', 'vitals:view', 'vitals:update'
      ],
      
      RECEPTIONIST: [
        'patients:create', 'patients:read', 'patients:update',
        'appointments:create', 'appointments:read', 'appointments:update', 'appointments:cancel',
        'staff:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'waiting-patients:view', 'waiting-patients:update',
        'doctor-triage:view',
        'pharmacy-queue:view',
        'lab-queue:view',
        'verification-queue:view',
        'treatments:view',
        'checkout:view', 'checkout:process',
        // Additional permissions for lab technician, pharmacy, patients, and admissions
        'laboratory:read', 'laboratory:manage',
        'lab-orders:create', 'lab-orders:read', 'lab-orders:update', 'lab-orders:delete',
        'pharmacy:read', 'pharmacy:create', 'pharmacy:manage',
        'prescriptions:create', 'prescriptions:read', 'prescriptions:update',
        'admissions:read', 'admissions:view', 'admissions:create', 'admissions:update', 'admissions:discharge',
        'medical-records:read', 'medical-records:update',
        'inventory:read', 'inventory:update',
        'reports:read',
        'insurance:verify',
        // Legacy permissions (for backward compatibility)
        'waiting-room:view',
        'patient-queue:view', 'patient-queue:update',
        'workflow:view', 'workflow:update',
        'checkin:view', 'checkin:update',
        'waiting-time:view'
      ],
      
      ACCOUNTANT: [
        'patients:read',
        'billing:create', 'billing:read', 'billing:update',
        'claims:create', 'claims:read', 'claims:update',
        'reports:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'checkout:view', 'checkout:process'
      ],
      
      RADIOLOGIST: [
        'patients:read', 'patients:update',
        'appointments:read',
        'lab-orders:create', 'lab-orders:read', 'lab-orders:update', 'lab-orders:delete',
        'laboratory:read', 'laboratory:manage',
        'medical-records:read', 'medical-records:update',
        'treatments:read', 'treatments:update',
        'prescriptions:read',
        'admissions:read', 'admissions:view',
        'reports:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'lab-queue:view', 'lab-queue:process',
        'verification-queue:view', 'verification-queue:verify',
        'checkout:view',
        // Legacy permissions (for backward compatibility)
        'verification:read',
        'workflow:view', 'workflow:update',
        'vitals:record', 'vitals:view', 'vitals:update'
      ],

      REPORTS: [
        'reports:read',
        'audit:read',
        'patients:read',
        'laboratory:read',
        'pharmacy:read',
        'admissions:read',
      ],
      
      PATIENT: [
        'patients:read', 'patients:update',
        'appointments:read', 'appointments:cancel',
        'treatments:read',
        'prescriptions:read',
        'billing:read',
        // Patient Workflow Module Permissions (aligned with module names)
        'treatments:view'
      ]
    };
    
    const userPermissions = permissions[userRole] || [];
    
    // Admin has all permissions
    if (userPermissions.includes('*:*')) return true;
    
    // Check specific permission
    return userPermissions.includes(permission);
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    updateUser,
    updateClinic,
    refreshProfile,
    refreshClinicSettings,
    fetchUserPermissions,
    hasRole,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};